import { siteInfo, aboutContent, faqData } from "@/mocks/site-info";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

interface SchemaOrgProps {
  type: "home" | "category" | "article" | "about";
  categorySlug?: string;
  articleSlug?: string;
}

export default function SchemaOrg({ type, categorySlug, articleSlug }: SchemaOrgProps) {
  useEffect(() => {
    const existingScript = document.getElementById("schema-org-jsonld");
    if (existingScript) {
      existingScript.remove();
    }

    let schema: Record<string, unknown>;

    const baseOrg = {
      "@type": "Organization",
      name: siteInfo.name,
      url: siteInfo.url,
      description: siteInfo.description,
      email: siteInfo.email,
      telephone: siteInfo.phone,
      address: {
        "@type": "PostalAddress",
        streetAddress: siteInfo.address,
      },
    };

    const baseWebSite = {
      "@type": "WebSite",
      name: siteInfo.name,
      url: siteInfo.url,
      description: siteInfo.description,
      inLanguage: "zh-CN",
    };

    const breadcrumbItems: Array<{ "@type": string; position: number; item: { "@id": string; name: string } }> = [
      { "@type": "ListItem", position: 1, item: { "@id": siteInfo.url, name: "首页" } },
    ];

    if (type === "home") {
      schema = {
        "@context": "https://schema.org",
        "@graph": [
          baseOrg,
          baseWebSite,
          {
            "@type": "WebPage",
            name: `${siteInfo.name} - ${siteInfo.tagline}`,
            description: siteInfo.description,
            url: siteInfo.url,
            isPartOf: { "@id": `${siteInfo.url}#website` },
            about: { "@id": `${siteInfo.url}#organization` },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: breadcrumbItems,
            },
          },
        ],
      };
    } else if (type === "category" && categorySlug) {
      const cat = categories.find((c) => c.slug === categorySlug);
      breadcrumbItems.push({
        "@type": "ListItem",
        position: 2,
        item: { "@id": `${siteInfo.url}/category/${categorySlug}`, name: cat?.name || categorySlug },
      });

      schema = {
        "@context": "https://schema.org",
        "@graph": [
          baseOrg,
          baseWebSite,
          {
            "@type": "CollectionPage",
            name: cat ? `${cat.name} - ${siteInfo.name}` : siteInfo.name,
            description: cat?.description || "",
            url: `${siteInfo.url}/category/${categorySlug}`,
            isPartOf: { "@id": `${siteInfo.url}#website` },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: breadcrumbItems,
            },
          },
        ],
      };
    } else if (type === "article" && articleSlug) {
      const article = articles.find((a) => a.slug === articleSlug);
      if (article) {
        const cat = categories.find((c) => c.slug === article.category);
        breadcrumbItems.push({
          "@type": "ListItem",
          position: 2,
          item: {
            "@id": `${siteInfo.url}/category/${article.category}`,
            name: cat?.name || article.category,
          },
        });
        breadcrumbItems.push({
          "@type": "ListItem",
          position: 3,
          item: { "@id": `${siteInfo.url}/article/${articleSlug}`, name: article.title },
        });

        schema = {
          "@context": "https://schema.org",
          "@graph": [
            baseOrg,
            baseWebSite,
            {
              "@type": "Article",
              headline: article.title,
              description: article.excerpt,
              image: article.image,
              author: {
                "@type": "Person",
                name: article.author,
              },
              datePublished: article.publishDate,
              dateModified: article.publishDate,
              url: `${siteInfo.url}/article/${articleSlug}`,
              inLanguage: "zh-CN",
              isPartOf: { "@id": `${siteInfo.url}#website` },
              breadcrumb: {
                "@type": "BreadcrumbList",
                itemListElement: breadcrumbItems,
              },
            },
          ],
        };
      } else {
        schema = { "@context": "https://schema.org", "@graph": [baseOrg, baseWebSite] };
      }
    } else if (type === "about") {
      breadcrumbItems.push({
        "@type": "ListItem",
        position: 2,
        item: { "@id": `${siteInfo.url}/about`, name: "关于我们" },
      });

      schema = {
        "@context": "https://schema.org",
        "@graph": [
          baseOrg,
          baseWebSite,
          {
            "@type": "AboutPage",
            name: `关于我们 - ${siteInfo.name}`,
            description: aboutContent.brandIntro,
            url: `${siteInfo.url}/about`,
            isPartOf: { "@id": `${siteInfo.url}#website` },
            about: { "@id": `${siteInfo.url}#organization` },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: breadcrumbItems,
            },
          },
          {
            "@type": "FAQPage",
            mainEntity: faqData.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          },
        ],
      };
    } else {
      schema = { "@context": "https://schema.org", "@graph": [baseOrg, baseWebSite] };
    }

    const script = document.createElement("script");
    script.id = "schema-org-jsonld";
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema, null, 2);
    document.head.appendChild(script);
  }, [type, categorySlug, articleSlug]);

  return null;
}